import React, { Component } from "react";
import ReactToPrint from "react-to-print";
import { Printer, Download } from "react-feather";

/* Shared invoice chrome and row primitives, ported from the customer app's
 * src/components/InvoiceShell.jsx so the admin copy of an invoice is the same
 * document the customer sees. That app is Tailwind; this one is not, so the
 * classes are expressed as inline styles here. */

const ORANGE = "#FF6A00";
const GREEN = "#00B42A";

export function InvoiceRow({ label, value, bold, tone, hint }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "4px 0",
      }}
    >
      <span
        style={{
          fontSize: 13,
          fontWeight: bold ? "bold" : "normal",
          color: bold ? "#111827" : "#6b7280",
        }}
      >
        {label}
        {hint && (
          <span
            style={{
              marginLeft: 6,
              fontSize: 10,
              fontStyle: "italic",
              color: "#9ca3af",
            }}
          >
            {hint}
          </span>
        )}
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: "bold",
          flexShrink: 0,
          color:
            tone === "green" ? GREEN : tone === "due" ? ORANGE : "#111827",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function InvoiceDivider() {
  return (
    <div
      style={{
        borderTop: "1px dashed #e5e7eb",
        margin: "8px 0",
      }}
    />
  );
}

export function InvoiceSection({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <p
        style={{
          fontSize: 10,
          fontWeight: 800,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          marginBottom: 8,
        }}
      >
        {title}
      </p>
      {children}
    </div>
  );
}

export function InvoiceBadge({ children, color = "gray" }) {
  const map = {
    gray: { bg: "#f1f2f4", text: "#5b5f66" },
    orange: { bg: "#fff1e6", text: ORANGE },
    emerald: { bg: "#e5f9e9", text: GREEN },
    amber: { bg: "#fef6e0", text: "#8a5a06" },
    red: { bg: "#fdeaea", text: "#F5222D" },
  };
  const c = map[color] || map.gray;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 11,
        fontWeight: "bold",
        padding: "4px 10px",
        borderRadius: 999,
        background: c.bg,
        color: c.text,
      }}
    >
      {children}
    </span>
  );
}

/* A label/value pair inside the two-column info grids. */
export function InvoiceField({ label, value, capitalize }) {
  return (
    <div>
      <p style={{ fontSize: 10, color: "#9ca3af", marginBottom: 2 }}>{label}</p>
      <p
        style={{
          fontSize: 13,
          fontWeight: "bold",
          color: "#1f2937",
          textTransform: capitalize ? "capitalize" : "none",
        }}
      >
        {value || "—"}
      </p>
    </div>
  );
}

export function InvoiceGrid({ children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

/* Carton numbers, shipping marks and tracking numbers are stored as arrays on
   newer records and as comma strings on older ones. Rendering the array
   straight into JSX is what produced "RK-902Industrial lookIndustrial look" -
   React concatenates array children with no separator. */
export const toInvoiceList = (value) => {
  const parts = Array.isArray(value)
    ? value
    : String(value || "").split(",");
  return parts.map((part) => String(part || "").trim()).filter(Boolean);
};

/* "0" is the placeholder written when no tracking number exists yet. */
export const isInvoicePlaceholder = (value) => value === "0";


export const InvoiceChip = ({ children }) => (
  <span
    style={{
      display: "inline-block",
      background: "#f1f5f9",
      color: "#1f2937",
      borderRadius: 6,
      padding: "3px 9px",
      fontSize: 12,
      fontWeight: "bold",
      marginRight: 6,
      marginBottom: 6,
    }}
  >
    {children}
  </span>
);

export const InvoiceChipRow = ({ label, items }) => (
  <div style={{ marginBottom: 10 }}>
    <p style={{ fontSize: 10, color: "#9ca3af", marginBottom: 4 }}>{label}</p>
    <div>
      {items.map((item, i) => (
        <InvoiceChip key={i}>{item}</InvoiceChip>
      ))}
    </div>
  </div>
);

/* One row per carton. Marks and carton numbers are written together, one
   entry per carton, so an equal-length pair lines up positionally - which
   reads far better than two long comma lists side by side. */
export const InvoiceCartonTable = ({ cartons, marks }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          {["#", "Carton No", "Shipping Mark"].map((h, i) => (
            <th
              key={h}
              style={{
                textAlign: "left",
                fontSize: 10,
                fontWeight: 800,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                padding: "0 10px 6px 0",
                width: i === 0 ? 28 : "auto",
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {cartons.map((carton, i) => (
          <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
            <td style={{ padding: "7px 10px 7px 0", fontSize: 12, color: "#9ca3af" }}>
              {i + 1}
            </td>
            <td
              style={{
                padding: "7px 10px 7px 0",
                fontSize: 13,
                fontWeight: "bold",
                color: "#1f2937",
                whiteSpace: "nowrap",
              }}
            >
              {carton}
            </td>
            <td style={{ padding: "7px 0", fontSize: 13, color: "#334155" }}>
              {marks[i] || "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);


const toolbarButton = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 16px",
  borderRadius: 12,
  fontSize: 13,
  fontWeight: "bold",
  cursor: "pointer",
  border: "1px solid #e5e7eb",
};

class InvoiceShell extends Component {
  state = { downloading: false };

  /* Rasterise the invoice card and slice it across A4 pages - same approach
     as the customer app's shell, so both produce the same document. */
  handleDownloadPdf = async () => {
    if (!this.printRef || this.state.downloading) return;
    this.setState({ downloading: true });
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const canvas = await html2canvas(this.printRef, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`Paicart-Invoice-${this.props.docId}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Could not generate the PDF. Try Print instead.");
    } finally {
      this.setState({ downloading: false });
    }
  };

  render() {
    const { logo, docType, docId, docDate, loading, notFound, children } =
      this.props;
    const { downloading } = this.state;
    return (
  <div style={{ background: "#f9fafb", padding: "24px 12px" }}>
    {/* A4 is 210mm wide; sizing the sheet in mm makes what is on screen the
        same width as what comes out of the printer and the PDF. */}
    <div style={{ width: "210mm", maxWidth: "100%", margin: "0 auto" }}>
      <div
        className="invoice-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 8,
          marginBottom: 16,
        }}
      >
        <ReactToPrint
          trigger={() => (
            <button
              style={{
                ...toolbarButton,
                background: "white",
                color: "#374151",
              }}
            >
              <Printer size={14} /> Print
            </button>
          )}
          content={() => this.printRef}
          documentTitle={`Paicart-Invoice-${docId}`}
        />
        <button
          onClick={this.handleDownloadPdf}
          disabled={downloading}
          style={{
            ...toolbarButton,
            background: "#FF6A00",
            color: "white",
            border: "1px solid #FF6A00",
            opacity: downloading ? 0.6 : 1,
          }}
        >
          <Download size={14} />
          {downloading ? "Preparing…" : "Download PDF"}
        </button>
      </div>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 256,
            color: "#9ca3af",
            fontSize: 13,
          }}
        >
          Loading invoice…
        </div>
      ) : (
        <div
          ref={(el) => (this.printRef = el)}
          className="invoice-sheet"
          style={{
            background: "white",
            borderRadius: 16,
            boxShadow: "0 1px 3px rgba(0,0,0,.08)",
            /* A4 side margin; minHeight keeps a short invoice looking like a
               full sheet rather than a floating strip. */
            padding: "15mm",
            minHeight: "297mm",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 24,
              paddingBottom: 24,
              borderBottom: "1px solid #f3f4f6",
            }}
          >
            <div>
              <img
                src={logo}
                alt="Paicart"
                style={{ height: 36, width: "auto", marginBottom: 6 }}
              />
              <p style={{ fontSize: 12, color: "#9ca3af" }}>{docType}</p>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <p style={{ fontWeight: 800, color: "#111827", fontSize: 15 }}>
                #{docId}
              </p>
              {docDate && (
                <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                  {docDate}
                </p>
              )}
            </div>
          </div>

          {notFound ? (
            <p
              style={{
                color: "#9ca3af",
                textAlign: "center",
                padding: "64px 0",
              }}
            >
              Not found.
            </p>
          ) : (
            children
          )}

          <div
            style={{
              marginTop: 32,
              paddingTop: 16,
              borderTop: "1px solid #f3f4f6",
              textAlign: "center",
              fontSize: 12,
              color: "#9ca3af",
            }}
          >
            Thank you for choosing Paicart! For support, contact us at
            support@paicart.com
          </div>
        </div>
      )}
    </div>
  </div>
    );
  }
}

export default InvoiceShell;
